import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import {
  Typography,
  IconButton,
  Switch,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Tooltip,
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import { CopyIcon } from 'Shared/Icons';
import { AlertDialog } from 'Shared/AlertDialog';
import FlowsGraph from 'Shared/flows/FlowsGraph';
import FlowPerformanceGraph from 'Shared/flows/FlowPerformanceGraph';
import FlowCopyForm from 'Shared/flows/flows-interface/FlowCopyForm';
import { setDragData } from 'Shared/flows/folders/folderUtils';
import FlowFolderLabel from 'Shared/flows/folders/FlowFolderLabel';
import http from 'Utils/http';
import FlowIdLabel from 'Shared/flows/FlowIdLabel';
import FlowRunButton from 'Shared/flows/FlowRunButton';

export const AdminFlowsItemV2 = ({ item, deleteFlow, changeReadonlyFlow, needUpdate, userRole, needUpdateCompanyFlows, onOpenCompanyAccessModal, selected, onToggleSelect, folderPath }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector(state => state.auth?.user);

  const [expanded, setExpanded] = useState(false);
  const [visibleCopyFlowForm, setVisibleCopyFlowForm] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const deleteText = 'Are you sure you want to delete flow in your list?';
  const changeText = 'Are you sure you want to change status flow?';
  const [modalText, setModalText] = useState('Are you sure you want to change status flow?');
  const [modalAction, setModalAction] = useState('change');

  const toFlow = (event, item) => {
    event.preventDefault();
    // Use user_id instead of current_user_id, as current_user_id may be missing
    const userId = item.current_user_id || item.user_id;
    navigate(`/flow/${item.id}/current_user/${userId}`);
  };

  const toggleExpanded = (e) => {
    e.stopPropagation();
    setExpanded((prev) => (prev === 'panel1' ? false : 'panel1'));
  };

  const handleChangeSwitch = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Admin: открыть modal управления доступом компаний
    if (userRole === 'Admin' && onOpenCompanyAccessModal) {
      onOpenCompanyAccessModal(item.id);
    }
    // CompanyAdmin: показать подтверждение перед toggle
    else if (userRole === 'Company Admin' && currentUser?.company_id) {
      const nextStatus = item.is_active_for_any_company ? 'disabled' : 'active';
      setModalText(`Are you sure you want to ${nextStatus === 'active' ? 'enable' : 'disable'} flow access for your company?`);
      setModalAction('access');
      setIsActiveModal(true);
    }
  };

  const toggleCompanyAccess = () => {
    const nextStatus = item.is_active_for_any_company ? 'disabled' : 'active';
    const params = {
      companies: [
        {
          company_id: currentUser.company_id,
          status: nextStatus
        }
      ]
    };

    dispatch(setLoading(true));
    http.post(`/flow/${item.id}/company-access`, params)
      .then(() => {
        if (needUpdateCompanyFlows) {
          needUpdateCompanyFlows();
        }
        enqueueSnackbar(`Flow access has been ${nextStatus === 'active' ? 'enabled' : 'disabled'}`, {
          action: CustomSnackbar,
          variant: 'success'
        });
      })
      .catch((err) => {
        enqueueSnackbar(`${err.response?.data?.message || 'Error updating flow access'}`, {
          action: CustomSnackbar,
          variant: 'error'
        });
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const handleChangeReadonlySwitch = (e) => {
    e.preventDefault();
    setModalText('Are you sure you want to change readonly status flow?');
    setModalAction('readonly');
    setIsActiveModal(true);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setModalText(deleteText);
    setModalAction('delete');
    setIsActiveModal(true);
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    setVisibleCopyFlowForm(true);
  };

  const [isActiveModal, setIsActiveModal] = useState(false);
  const onClose = () => setIsActiveModal(false);
  const onAgree = (e) => {
    setIsActiveModal(false);
    if (modalAction === 'delete') {
      deleteFlow(item);
    }
    if (modalAction === 'readonly') {
      changeReadonlyFlow(item);
    }
    if (modalAction === 'access') {
      toggleCompanyAccess();
    }
  };

  return (
    <>
      <AlertDialog visible={isActiveModal} onClose={onClose} onAgree={onAgree} text={modalText} />
      <FlowCopyForm flowName={item.name}
        id={item.id}
        needUpdateList={needUpdate}
        needUpdateCompanyFlows={needUpdateCompanyFlows}
        setVisibleFlowForm={setVisibleCopyFlowForm}
        visibleFlowForm={visibleCopyFlowForm}
      />
      <Accordion
        sx={{
          marginBottom: '20px',
        }}
        expanded={expanded === 'panel1'}
      >
        <AccordionSummary
          draggable
          onDragStart={(e) => setDragData(e, { kind: 'flow', id: item.id })}
          sx={{
            backgroundColor: '#F2F5F8',
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
            },
          }}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
        >
          {onToggleSelect && (
            <Checkbox
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleSelect(item)}
              sx={{ marginRight: '8px', flexShrink: 0 }}
            />
          )}
          <FlowIdLabel id={item.id} externalId={item.external_id} />
          <Box
            sx={{ flex: 1, minWidth: 0, marginRight: '20px', cursor: 'pointer' }}
            onClick={(e) => toFlow(e, item)}
          >
            <FlowFolderLabel path={folderPath} />
            <Typography
              variant="h6"
              sx={{
                color: '#3B465C',
                overflowWrap: 'anywhere',
              }}
            >
              {item?.name}
            </Typography>
            {item?.description && item.description !== item.name && (
              <Typography
                variant="body2"
                sx={{ color: '#888', fontSize: '0.8rem', overflowWrap: 'anywhere' }}
              >
                {item.description}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginLeft: 'auto',
              flexShrink: 0,
            }}
          >
            <Tooltip title={
              <span style={{fontSize: '1rem', lineHeight: '120%'}}>Make the flow visible to all company users</span>
            } arrow>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontSize: '0.75rem',
                    marginRight: '8px',
                    fontWeight: 500
                  }}
                >
                  Access
                </Typography>
                <Switch
                  checked={item.is_active_for_any_company || false}
                  color="primary"
                  onClick={(e) => handleChangeSwitch(e)}
                />
              </Box>
            </Tooltip>

            {(userRole === 'Admin' || userRole === 'Company Admin') && (
              <Tooltip title={
                                    <span style={{fontSize: '1rem', lineHeight: '120%'}}>Prevent any company user except the flow owner from editing the flow</span>
              } arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '16px' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666',
                      fontSize: '0.75rem',
                      marginRight: '8px',
                      fontWeight: 500
                    }}
                  >
                    Read only
                  </Typography>
                  <Switch
                      checked={item.readonly === 1 || item.readonly === '1' || item.readonly === true}
                      color="primary"
                      onClick={handleChangeReadonlySwitch}
                  />
                </Box>
              </Tooltip>
            )}

            <Tooltip title={
              <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Copy Flow</span>
            } arrow>
              <IconButton
                sx={{
                  color: '#f44336',
                }}
                onClick={handleCopy}
                aria-label="copy"
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={
              <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Usage Statistics</span>
            } arrow>
              <IconButton
                  sx={{ marginRight: '14px', color: expanded ? '#F4B156' : '' }}
                  aria-label="open-chart"
                  onClick={toggleExpanded}
              >
                <StackedBarChartIcon />
              </IconButton>
            </Tooltip>

            <IconButton
              sx={{
                color: '#f44336',
              }}
              onClick={(e) => {
                handleDelete(e);
              }}
              aria-label="delete"
            >
              <DeleteIcon />
            </IconButton>

            <FlowRunButton flow={item} />
            <Tooltip title={
              <span style={{ fontSize: '1rem', lineHeight: '120%' }}>Edit Flow</span>
            } arrow>
              <IconButton
                onClick={(e) => {
                  toFlow(e, item);
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {expanded ? (
            <Box>
              <Tabs
                value={selectedTab}
                onChange={(e, newValue) => setSelectedTab(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}
              >
                <Tab label="API Requests" />
                <Tab label="Performance" />
              </Tabs>
              {selectedTab === 0 && <FlowsGraph item={item}/>}
              {selectedTab === 1 && <FlowPerformanceGraph item={item}/>}
            </Box>
          ) : null}
        </AccordionDetails>
      </Accordion>
    </>
  );
};
